import React, { useState, useEffect } from 'react';
import { getActivityLogs, getLearnersById } from '../api';
import ExportModal from './exportmodal';
import './Monitor.css';

const getCourseEnrollmentCounts = (enrolments = []) =>
  enrolments
    .filter((enrollment) => enrollment?.course && !enrollment?.test && !enrollment?.quiz)
    .reduce(
      (counts, enrollment) => {
        const status = String(enrollment?.status || '').toLowerCase();

        if (status === 'completed') {
          counts.completed += 1;
        } else if (status === 'active') {
          counts.pending += 1;
        }

        return counts;
      },
      { completed: 0, pending: 0 }
    );

const Monitor = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredUserId, setFilteredUserId] = useState(null);
  const [filteredUserName, setFilteredUserName] = useState(null);
  const [learnerDetails, setLearnerDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('courses');
  const [showExportModal, setShowExportModal] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  useEffect(() => {
    fetchActivityLogs(currentPage, filteredUserId);
  }, [currentPage, filteredUserId]);

  useEffect(() => {
    if (filteredUserId) {
      fetchLearnerDetails(filteredUserId);
    } else {
      setLearnerDetails(null);
    }
  }, [filteredUserId]);

  const fetchActivityLogs = async (page, causerId = null) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getActivityLogs(page, 15, causerId);
      
      setLogs(response.data || []);
      setPagination({
        current_page: response.current_page || 1,
        last_page: response.last_page || 1,
        per_page: response.per_page || 15,
        total: response.total || 0,
      });
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setError(err?.error || err?.message || 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchLearnerDetails = async (learnerId) => {
    try {
      setLoadingDetails(true);
      const learner = await getLearnersById(learnerId);
      setLearnerDetails(learner);
    } catch (err) {
      console.error('Error fetching learner details:', err);
      // Don't show error, just log it - learner details are optional
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return dateString;
    }
  };

  const getUserName = (causer) => {
    if (!causer) return 'Unknown User';
    if (causer.full_name) return causer.full_name;
    if (causer.firstname || causer.lastname) {
      return `${causer.firstname || ''} ${causer.lastname || ''}`.trim();
    }
    return causer.email || causer.phone_number || 'Unknown User';
  };

  const getUserType = (causer) => {
    if (!causer) return 'N/A';
    if (causer.is_admin === 1) return 'ADMIN';
    return 'LEARNER';
  };

  const getActivityDescription = (log) => {
    // Extract model name from subject_type (e.g., "App\\Models\\Test" -> "Test")
    const getModelName = (subjectType) => {
      if (!subjectType) return null;
      // Handle Laravel model paths like "App\\Models\\Test" or "App\Models\Test"
      const parts = subjectType.split(/[\\\/]/);
      return parts[parts.length - 1] || subjectType;
    };

    const modelName = getModelName(log.subject_type);
    const logName = (log.log_name || '').toLowerCase();
    const description = log.description || '';
    const subject = log.subject || {};

    // Map model names to user-friendly terms
    const modelDisplayNames = {
      'Test': 'Test',
      'Quiz': 'Quiz',
      'Course': 'Course',
      'CourseEnrollment': 'Course Enrollment',
      'Enrollment': 'Course Enrollment',
      'User': 'User',
      'Learner': 'Learner',
      'Admin': 'Admin',
      'Module': 'Module',
      'Topic': 'Topic',
      'Question': 'Question',
      'Forum': 'Forum Post',
      'Discussion': 'Discussion',
      'Feedback': 'Feedback',
      'Certificate': 'Certificate',
    };

    const displayModelName = modelDisplayNames[modelName] || modelName || 'Item';

    // Get subject details if available
    const getSubjectDetails = () => {
      if (subject.title) return ` "${subject.title}"`;
      if (subject.name) return ` "${subject.name}"`;
      if (subject.full_name) return ` "${subject.full_name}"`;
      if (subject.email) return ` "${subject.email}"`;
      if (subject.course?.title) return ` "${subject.course.title}"`;
      if (subject.test?.title) return ` "${subject.test.title}"`;
      if (subject.quiz?.title) return ` "${subject.quiz.title}"`;
      return '';
    };

    const subjectDetails = getSubjectDetails();

    // Handle login/logout (no subject_type needed)
    if (logName.includes('login') || description.toLowerCase().includes('login')) {
      return 'Logged in';
    }
    if (logName.includes('logout') || description.toLowerCase().includes('logout')) {
      return 'Logged out';
    }

    // Special handling for specific activities based on model and action
    if (modelName === 'Test') {
      if (logName.includes('attempt') || logName.includes('submit') || logName.includes('complete')) {
        return `Attempted test${subjectDetails}`;
      }
      if (logName.includes('created')) {
        return `Created test${subjectDetails}`;
      }
      if (logName.includes('updated')) {
        return `Updated test${subjectDetails}`;
      }
      return `Test activity${subjectDetails}`;
    }

    if (modelName === 'CourseEnrollment' || modelName === 'Enrollment') {
      // Check if it's a test enrollment or course enrollment
      const isTestEnrollment = subject.test || subject.test_id || subject.quiz || subject.quiz_id || 
                                description.toLowerCase().includes('test') || 
                                description.toLowerCase().includes('quiz');
      
      if (logName.includes('created') || logName.includes('enroll')) {
        return isTestEnrollment 
          ? `Enrolled in test${subjectDetails}` 
          : `Enrolled in course${subjectDetails}`;
      }
      if (logName.includes('complete') || logName.includes('finished')) {
        return isTestEnrollment 
          ? `Completed test${subjectDetails}` 
          : `Completed course${subjectDetails}`;
      }
      if (logName.includes('updated')) {
        return isTestEnrollment 
          ? `Updated test enrollment${subjectDetails}` 
          : `Updated course enrollment${subjectDetails}`;
      }
      return isTestEnrollment 
        ? `Test enrollment activity${subjectDetails}` 
        : `Course enrollment activity${subjectDetails}`;
    }

    if (modelName === 'Course') {
      if (logName.includes('created')) {
        return `Created course${subjectDetails}`;
      }
      if (logName.includes('updated')) {
        return `Updated course${subjectDetails}`;
      }
      if (logName.includes('enroll')) {
        return `Enrolled in course${subjectDetails}`;
      }
      return `Course activity${subjectDetails}`;
    }

    if (modelName === 'Question') {
      if (logName.includes('created')) {
        return `Created question${subjectDetails}`;
      }
      if (logName.includes('updated')) {
        return `Updated question${subjectDetails}`;
      }
      if (logName.includes('deleted')) {
        return `Deleted question${subjectDetails}`;
      }
      return `Question activity${subjectDetails}`;
    }

    // Generic handling for other models
    let action = '';
    if (logName.includes('created')) {
      action = 'Created';
    } else if (logName.includes('updated')) {
      action = 'Updated';
    } else if (logName.includes('deleted')) {
      action = 'Deleted';
    } else if (logName.includes('complete') || logName.includes('finished')) {
      action = 'Completed';
    } else if (logName.includes('attempt') || logName.includes('submit')) {
      action = 'Attempted';
    } else if (logName.includes('enroll')) {
      // Check if it's a test enrollment or course enrollment
      const isTestEnrollment = subject.test || subject.test_id || subject.quiz || subject.quiz_id || 
                                description.toLowerCase().includes('test') || 
                                description.toLowerCase().includes('quiz');
      action = isTestEnrollment ? 'Enrolled in test' : 'Enrolled in course';
    } else if (description && description !== 'created' && description !== 'updated' && description !== 'deleted') {
      // Use description if it's meaningful
      return description;
    } else {
      // Use the log_name as-is if it's meaningful
      action = (log.log_name || '').charAt(0).toUpperCase() + (log.log_name || '').slice(1) || 'Activity';
    }

    // Default format: "Action ModelName [details]"
    return `${action} ${displayModelName}${subjectDetails}`;
  };

  const handleRowClick = (log) => {
    if (log.causer_id) {
      setFilteredUserId(log.causer_id);
      setFilteredUserName(getUserName(log.causer));
      setCurrentPage(1); // Reset to first page when filtering
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleClearFilter = () => {
    setFilteredUserId(null);
    setFilteredUserName(null);
    setLearnerDetails(null);
    setActiveTab('courses');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      setCurrentPage(newPage);
    }
  };

  const courseEnrollmentCounts = getCourseEnrollmentCounts(
    learnerDetails?.enrolments || []
  );

  if (loading && logs.length === 0) {
    return (
      <div className="monitor-page">
        <div className="monitor-header">
          <h1>Activity Monitor</h1>
        </div>
        <div className="monitor-loading">
          <p>Loading activity logs...</p>
        </div>
      </div>
    );
  }

  if (error && logs.length === 0) {
    return (
      <div className="monitor-page">
        <div className="monitor-header">
          <h1>Activity Monitor</h1>
        </div>
        <div className="monitor-error">
          <p>❌ {error}</p>
          <button onClick={() => fetchActivityLogs(currentPage)} className="btn-retry">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="monitor-page">
      <div className="monitor-header">
        <div className="header-left">
          {filteredUserId && (
            <button onClick={handleClearFilter} className="btn-back">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
              Back to All Activities
            </button>
          )}
          <div>
            <h1>Activity Monitor</h1>
            <p className="monitor-subtitle">
              {filteredUserId 
                ? `Showing activities for: ${filteredUserName}` 
                : 'Track all user activities on the platform'}
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => setShowExportModal(true)}
            className="btn-export"
            title="Export activity logs"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13v6H5v-6H3v8h18v-8h-2zm-6-10h-2v8.586L6.707 7.293 5.293 8.707 12 15.414l6.707-6.707-1.414-1.414L13 11.586V3z"/>
            </svg>
            Export {filteredUserId ? 'User Activities' : 'Activity Logs'}
          </button>
        </div>
      </div>

      {filteredUserId && learnerDetails && (
        <div className="learner-details-section">
          <h2 className="section-title">Learner Information</h2>
          <div className="learner-info-grid">
            <div className="info-item">
              <span className="info-label">Name:</span>
              <span className="info-value">
                {learnerDetails.full_name || 
                 `${learnerDetails.firstname || ''} ${learnerDetails.lastname || ''}`.trim() || 
                 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Email:</span>
              <span className="info-value">{learnerDetails.email || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Phone:</span>
              <span className="info-value">{learnerDetails.phone_number || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">LGA:</span>
              <span className="info-value">
                {learnerDetails.lga?.name || learnerDetails.lga_name || 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Facility:</span>
              <span className="info-value">
                {learnerDetails.facility?.name || learnerDetails.facility_name || 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Completed Courses:</span>
              <span className="info-value highlight">{courseEnrollmentCounts.completed}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Pending Courses:</span>
              <span className="info-value highlight">{courseEnrollmentCounts.pending}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Tests Taken:</span>
              <span className="info-value highlight">{learnerDetails.tests_count || 0}</span>
            </div>
            {learnerDetails.tests_avg_percentile && (
              <div className="info-item">
                <span className="info-label">Average Score:</span>
                <span className="info-value highlight score">
                  {learnerDetails.tests_avg_percentile.toFixed(1)}%
                </span>
              </div>
            )}
          </div>

          {learnerDetails.enrolments && learnerDetails.enrolments.length > 0 && (
            <div className="enrollments-section">
              <div className="enrollments-tabs">
                <button 
                  className={`tab-btn ${activeTab === 'courses' ? 'active' : ''}`}
                  onClick={() => setActiveTab('courses')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  Courses ({learnerDetails.enrolments.filter(e => e.course && !e.test && !e.quiz).length})
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'quizzes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('quizzes')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                  </svg>
                  Quizzes ({learnerDetails.tests_count || 0})
                </button>
              </div>

              {activeTab === 'courses' ? (
                <div className="courses-container">
                  {(() => {
                    // Filter course enrollments only (not test enrollments)
                    const courseEnrollments = learnerDetails.enrolments
                      .filter(enrollment => enrollment.course && !enrollment.test && !enrollment.quiz)
                      .sort((a, b) => {
                        // Sort by most recent first
                        const dateA = new Date(a.created_at || a.updated_at || 0);
                        const dateB = new Date(b.created_at || b.updated_at || 0);
                        return dateB - dateA;
                      });
                    
                    const mostRecentEnrollment = courseEnrollments[0];
                    
                    return (
                      <>
                        {mostRecentEnrollment && (
                          <div className="most-recent-enrollment">
                            <h3 className="section-subtitle">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                              </svg>
                              Most Recent Enrollment
                            </h3>
                            <div className="recent-enrollment-card">
                              <div className="recent-enrollment-header">
                                <h4 className="recent-enrollment-title">
                                  {mostRecentEnrollment.course?.title || 'Unknown Course'}
                                </h4>
                                <span className={`recent-enrollment-status ${mostRecentEnrollment.status || 'active'}`}>
                                  {mostRecentEnrollment.status || 'active'}
                                </span>
                              </div>
                              <div className="recent-enrollment-progress-section">
                                <div className="progress-header">
                                  <span className="progress-label">Progress</span>
                                  <span className="progress-percentage">
                                    {mostRecentEnrollment.progress !== undefined 
                                      ? `${mostRecentEnrollment.progress}%`
                                      : mostRecentEnrollment.progress_percentage !== undefined
                                      ? `${mostRecentEnrollment.progress_percentage}%`
                                      : mostRecentEnrollment.status === 'completed'
                                      ? '100%'
                                      : '0%'}
                                  </span>
                                </div>
                                <div className="progress-bar">
                                  <div 
                                    className={`progress-fill ${mostRecentEnrollment.status === 'completed' ? 'completed' : 'in-progress'}`}
                                    style={{ 
                                      width: mostRecentEnrollment.progress !== undefined 
                                        ? `${mostRecentEnrollment.progress}%`
                                        : mostRecentEnrollment.progress_percentage !== undefined
                                        ? `${mostRecentEnrollment.progress_percentage}%`
                                        : mostRecentEnrollment.status === 'completed'
                                        ? '100%'
                                        : '0%'
                                    }}
                                  ></div>
                                </div>
                                <div className="progress-details">
                                  <span className="progress-detail-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
                                    </svg>
                                    Enrolled: {mostRecentEnrollment.created_at 
                                      ? new Date(mostRecentEnrollment.created_at).toLocaleDateString('en-US', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric'
                                        })
                                      : 'N/A'}
                                  </span>
                                  {mostRecentEnrollment.completed_at && (
                                    <span className="progress-detail-item">
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                      </svg>
                                      Completed: {new Date(mostRecentEnrollment.completed_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="all-enrollments-section">
                          <h3 className="section-subtitle">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                            </svg>
                            All Course Enrollments ({courseEnrollments.length})
                          </h3>
                  <div className="enrollments-grid">
                            {courseEnrollments.map((enrollment) => {
                              const progress = enrollment.progress !== undefined 
                                ? enrollment.progress 
                                : enrollment.progress_percentage !== undefined
                                ? enrollment.progress_percentage
                                : enrollment.status === 'completed'
                                ? 100
                                : 0;
                              
                              return (
                        <div key={enrollment.id} className="enrollment-card course-card">
                          <div className="card-icon course-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                            </svg>
                          </div>
                          <div className="enrollment-header">
                            <h4 className="enrollment-course-title">
                              {enrollment.course?.title || 'Unknown Course'}
                            </h4>
                            <span className={`enrollment-status ${enrollment.status || 'active'}`}>
                              {enrollment.status || 'active'}
                            </span>
                          </div>
                          {enrollment.course?.description && (
                            <p className="enrollment-description">
                              {enrollment.course.description.length > 120
                                ? `${enrollment.course.description.substring(0, 120)}...`
                                : enrollment.course.description}
                            </p>
                          )}
                          <div className="enrollment-progress">
                                    <div className="progress-header-small">
                                      <span className="progress-label-small">Progress</span>
                                      <span className="progress-percentage-small">{progress}%</span>
                                    </div>
                            <div className="progress-bar">
                              <div 
                                className={`progress-fill ${enrollment.status === 'completed' ? 'completed' : 'in-progress'}`}
                                        style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="enrollment-meta">
                            <span className="enrollment-date">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
                              </svg>
                              {enrollment.created_at 
                                ? new Date(enrollment.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })
                                : 'N/A'}
                            </span>
                          </div>
                        </div>
                              );
                            })}
                          </div>
                  </div>
                        
                        {courseEnrollments.length === 0 && (
                    <div className="no-enrollments">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      <p>No course enrollments found</p>
                    </div>
                  )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="quizzes-container">
                  <div className="quiz-stats">
                    <div className="quiz-stat-card">
                      <div className="quiz-stat-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                        </svg>
                      </div>
                      <div className="quiz-stat-content">
                        <span className="quiz-stat-label">Total Tests</span>
                        <span className="quiz-stat-value">{learnerDetails.tests_count || 0}</span>
                      </div>
                    </div>
                    {learnerDetails.tests_avg_percentile && (
                      <div className="quiz-stat-card highlight-card">
                        <div className="quiz-stat-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                        </div>
                        <div className="quiz-stat-content">
                          <span className="quiz-stat-label">Average Score</span>
                          <span className="quiz-stat-value">{learnerDetails.tests_avg_percentile.toFixed(1)}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="quiz-info-message">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    <p>Quiz and test details are tracked separately. Check the activity logs for specific quiz attempts.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {learnerDetails.enrolments && learnerDetails.enrolments.length === 0 && (
            <div className="no-enrollments">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              <p>No course or quiz enrollments found for this learner.</p>
            </div>
          )}
        </div>
      )}

      {filteredUserId && loadingDetails && (
        <div className="loading-details">
          <p>Loading learner details...</p>
        </div>
      )}

      <div className="monitor-stats">
        <div className="stat-card">
          <span className="stat-label">Total Activities</span>
          <span className="stat-value">{pagination.total}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Current Page</span>
          <span className="stat-value">{pagination.current_page} / {pagination.last_page}</span>
        </div>
      </div>

      <div className="monitor-table-container">
        <table className="monitor-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>User</th>
              <th>Type</th>
              <th>Activity</th>
              <th>Description</th>
              <th>Date & Time</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  No activity logs found
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr 
                  key={log.id} 
                  className="clickable-row"
                  onClick={() => handleRowClick(log)}
                  title="Click to view all activities for this user"
                >
                  <td>{log.id}</td>
                  <td>
                    <div className="user-info">
                      <span className="user-name">{getUserName(log.causer)}</span>
                      {log.causer?.email && (
                        <span className="user-email">{log.causer.email}</span>
                      )}
                      {!log.causer?.email && log.causer?.phone_number && (
                        <span className="user-email">{log.causer.phone_number}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`user-type-badge ${getUserType(log.causer).toLowerCase()}`}>
                      {getUserType(log.causer)}
                    </span>
                  </td>
                  <td>
                    <span className="log-name">{log.log_name || 'default'}</span>
                  </td>
                  <td>
                    <span className="description">{getActivityDescription(log)}</span>
                  </td>
                  <td>
                    <span className="timestamp">{formatDate(log.created_at)}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        context={{
          type: 'activity-logs',
          logs: logs,
          filteredUserId: filteredUserId,
          filteredUserName: filteredUserName,
          pagination: pagination,
          learnerDetails: learnerDetails
        }}
      />

      {pagination.last_page > 1 && (
        <div className="monitor-pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.current_page} of {pagination.last_page}
          </span>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === pagination.last_page}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Monitor;

